/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IDialogContentStyleProps, IDialogContentStyles } from '@fluentui/react/lib/Dialog';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { useFormState } from 'src/core/hooks/useFormState';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { logNextWizardStep, logProjectCreated } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { FormValidators } from 'src/core/utils/formValidators';
import { withModalMounter } from 'src/core/utils/hocs/withModalMounter';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { QnaMakerProjectStore } from 'src/qnaMaker/core/stores/QnaMakerProjectStore';
import { QnaMakerLanguage } from 'src/qnaMaker/core/types/QnaMakerLanguages';
import { QnaMakerChooseLanguageSettingScreen } from 'src/qnaMaker/verticals/qnaMakerProjectCreationModal/components/QnaMakerChooseLanguageSettingScreen';
import { QnaMakerEnterBasicInfoScreen } from 'src/qnaMaker/verticals/qnaMakerProjectCreationModal/components/QnaMakerEnterBasicInformationScreen';
import { QnaMakerProjectModalNavigator } from 'src/qnaMaker/verticals/qnaMakerProjectCreationModal/components/QnaMakerProjectModalNavigator';
import { QnaMakerReviewAndFinishScreen } from 'src/qnaMaker/verticals/qnaMakerProjectCreationModal/components/QnaMakerReviewAndFinishScreen';

export type QnaMakerLanguageSettings = { setting: 'multiLingual' | 'unset' } | { setting: 'singleLanguage'; language: QnaMakerLanguage };

export type QnaMakerProjectCreationModalProps = BasicModalProps<object> & {
    languageSettings?: QnaMakerLanguageSettings;
};

export type QnaMakerProjectDto = Pick<QnaMakerProjectStore, 'name' | 'description' | 'language' | 'defaultAnswer' | 'multilingualResource'>;

export enum QnaMakerProjectCreationSteps {
    ChooseLanguageSetting = 0,
    EnterBasicInformation = 1,
    ReviewAndFinish = 2
}

const messages = defineMessages({
    modalTitle: { id: 'qnaMaker.projectCreationModal.modalTitle', defaultMessage: 'Create a project' },
    nameErrorMessage: {
        id: 'qnaMaker.projectCreationModal.nameErrorMessage',
        defaultMessage: '`Invalid characters: You can only use letters (a-z, A-Z), numbers (0-9) and hyphens.`'
    }
});

const { styleComponent } = getStylistV2('ProjectCreationModalBase');
const modalTheme = getScopedTheme('ProjectCreationModalBase');

const borderStyle = `solid 1px ${modalTheme.borderColor}`;

export const QnaMakerModalScreen = styleComponent<IStackProps>(Stack)('QnaMakerModalScreen', {
    flex: 1,
    overflowY: 'auto',
    borderBottom: borderStyle,
    padding: '0 20px 20px 20px'
});
const dialogContentStyles = classNamesFunction<IDialogContentStyleProps, IDialogContentStyles>()({
    inner: { borderTop: borderStyle, padding: 0 }
});
const ContentWrapper = styleComponent<IStackProps>(Stack)('ContentWrapper', () => ({
    paddingTop: 15,
    marginRight: 10,
    minHeight: 600,
    width: '100%',
    minWidth: 0,
    position: 'relative'
}));
const NavigatorWrapper = styleComponent(Stack.Item)(
    'NavigatorWrapper',
    (_, cssVars) => ({
        width: cssVars.width,
        borderRight: cssVars.borderRight,
        padding: 20
    }),
    {
        cssVars: { width: '', borderRight: '' },
        cssVarsSetter: ({ horizontal }: { horizontal: boolean }) => ({
            width: horizontal ? '200px' : '',
            borderRight: horizontal ? borderStyle : ''
        })
    }
);

export const QnaMakerProjectCreationModal = withModalMounter(
    observer((props: QnaMakerProjectCreationModalProps) => {
        const { onSubmit, onDismiss, languageSettings = { setting: 'unset' } } = props;
        const { localize } = useLocalization();
        const { serviceName } = getPortalFromUrl();
        const { rootStore, dispatcher } = useGlobalState();
        const { csAccountStore: languageResource } = rootStore.azureStore.session;

        const { isLargeOrGreater } = useResponsiveMode();
        const { formStateDto, updateFormState } = useFormState<QnaMakerProjectDto>({
            multilingualResource: [languageSettings.setting === 'multiLingual'],
            name: [
                '',
                FormValidators.createRequiredValidator(),
                FormValidators.createBlacklistValidator({
                    errorMessage: messages.nameErrorMessage,
                    characterList: `\\\\/\:\?\&\=\*\+\(\)\%\$\~\!\>\<\%\@\#\_\\\,\'\.\^\\[\\]\ `
                })
            ],
            description: [''],
            language: [
                languageSettings.setting === 'singleLanguage'
                    ? (languageSettings as { setting: 'singleLanguage'; language: QnaMakerLanguage }).language
                    : null
            ],
            defaultAnswer: ['']
        });

        const [activeStep, setActiveStep] = React.useState(
            languageSettings.setting === 'unset'
                ? QnaMakerProjectCreationSteps.ChooseLanguageSetting
                : QnaMakerProjectCreationSteps.EnterBasicInformation
        );

        const projectCreationTracker = useProgressTracker({ onComplete: () => onSubmit(formStateDto) });

        const onNextStep = (stepInput?: Partial<QnaMakerProjectDto>) => {
            if (activeStep === QnaMakerProjectCreationSteps.ReviewAndFinish) {
                dispatcher.dispatch('createQnaMakerProject', {
                    projectMetaData: formStateDto,
                    tracker: projectCreationTracker,
                    toastData: {}
                });
                logProjectCreated(serviceName, stepInput?.name);
            } else {
                updateFormState(stepInput);
                setActiveStep(activeStep + 1);
                logNextWizardStep(serviceName, 'CreateProject', QnaMakerProjectCreationSteps[activeStep + 1], '');
            }
        };
        const onDismissStep = (stepName: number) => {
            onDismiss();
            logNextWizardStep(serviceName, 'CreateProject', QnaMakerProjectCreationSteps[stepName], 'Cancel');
        };

        const onPreviousStep = () => {
            if (activeStep === QnaMakerProjectCreationSteps.EnterBasicInformation && languageSettings.setting !== 'unset') {
                setActiveStep(QnaMakerProjectCreationSteps.EnterBasicInformation);
            } else {
                setActiveStep(activeStep - 1);
            }
        };

        const getStepContent = () => {
            switch (activeStep) {
                case QnaMakerProjectCreationSteps.ChooseLanguageSetting:
                    return (
                        <QnaMakerChooseLanguageSettingScreen
                            projectDto={formStateDto}
                            onDismiss={() => onDismissStep(activeStep)}
                            onNext={onNextStep}
                            languageResource={languageResource}
                        />
                    );
                case QnaMakerProjectCreationSteps.EnterBasicInformation:
                    return (
                        <QnaMakerEnterBasicInfoScreen
                            projectDto={formStateDto}
                            onDismiss={() => onDismissStep(activeStep)}
                            onPrevious={languageSettings.setting === 'unset' && onPreviousStep}
                            onNext={onNextStep}
                        />
                    );
                case QnaMakerProjectCreationSteps.ReviewAndFinish:
                    return (
                        <QnaMakerReviewAndFinishScreen
                            onSubmit={onNextStep}
                            onDismiss={() => onDismissStep(activeStep)}
                            onPrevious={onPreviousStep}
                            projectDto={formStateDto}
                            languageResource={languageResource}
                            projectCreationTracker={projectCreationTracker}
                        />
                    );
                default:
            }
        };

        return (
            <BasicModal
                {...props}
                minWidth={850}
                shouldRenderFooter={false}
                disabled={projectCreationTracker.isRunning}
                dialogContentProps={{ title: localize(messages.modalTitle), styles: dialogContentStyles }}>
                <Stack horizontal={isLargeOrGreater} verticalFill>
                    <NavigatorWrapper customProps={{ horizontal: isLargeOrGreater }}>
                        <QnaMakerProjectModalNavigator
                            activeStep={activeStep}
                            horizontal={!isLargeOrGreater}
                            onActiveStepChange={setActiveStep}
                            isLanguageSettingVisible={languageSettings.setting === 'unset'}
                        />
                    </NavigatorWrapper>

                    <ContentWrapper grow verticalFill>
                        {getStepContent()}
                    </ContentWrapper>
                </Stack>
            </BasicModal>
        );
    })
);
